import { Avatar, AvatarGroup, Badge, Box, ListItemButton, Stack, Typography } from '@mui/material'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { useAtomValue } from 'jotai'
import React from 'react'

import { currentUserAtom } from '../../domains/user/AtomUsers'
import { avatarUrl, UserAvatar } from '../../utils/gravatar'
import { useUsers } from '../hooks/useUsers'

dayjs.extend(relativeTime)

/**
 * ChatListItem - Individual conversation item in the chat list
 *
 * @param {Object} conversation - Conversation object
 * @param {boolean} selected - Whether this conversation is selected
 * @param {function} onClick - Callback when item is clicked
 * @param {Object} sx - Additional styles to apply
 */
export default function ChatListItem({ conversation, selected = false, onClick, sx = {} }) {
  const { title, participants = [], lastMessage, lastMessageTime, unreadCount = 0 } = conversation

  const currentUser = useAtomValue(currentUserAtom)
  const { users } = useUsers(participants.map((p) => p.id))

  // Check if it's a group conversation (more than 2 participants)
  const isGroup = participants.length > 2
  // Not supported

  const otherUser = users.filter((user) => user.resourceId !== currentUser.resourceId)[0]

  // Format timestamp
  const getTimeLabel = (timestamp) => {
    if (!timestamp) return ''
    const msgTime = dayjs(timestamp)
    const now = dayjs()

    if (msgTime.isSame(now, 'day')) {
      return msgTime.format('HH:mm')
    } else if (msgTime.isSame(now.subtract(1, 'day'), 'day')) {
      return 'Yesterday'
    } else if (msgTime.isAfter(now.subtract(7, 'day'))) {
      return msgTime.format('ddd')
    } else {
      return msgTime.format('MM/DD')
    }
  }

  // Truncate message text
  const truncateText = (text, maxLength = 40) => {
    if (!text) return ''
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text
  }

  return (
    <ListItemButton
      selected={selected}
      onClick={onClick}
      sx={{
        px: 2,
        py: 1.5,
        maxHeight: '80px',
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&.Mui-selected': {
          bgcolor: 'action.selected',
          '&:hover': {
            bgcolor: 'action.selected',
          },
        },
        '&:hover': {
          bgcolor: 'action.hover',
        },
        ...sx,
      }}
    >
      <Stack direction="row" spacing={2} sx={{ width: '100%', alignItems: 'center' }}>
        <Badge
          badgeContent={unreadCount}
          color="primary"
          max={99}
          sx={{
            '& .MuiBadge-badge': {
              right: -3,
              top: 3,
            },
          }}
        >
          {isGroup ? (
            <AvatarGroup
              max={3}
              sx={{
                '& .MuiAvatar-root': {
                  width: 32,
                  height: 32,
                  fontSize: '0.875rem',
                  border: '2px solid',
                  borderColor: 'background.paper',
                },
              }}
            >
              {participants.slice(0, 4).map((participant, index) => (
                <Avatar key={participant.id || index} src={avatarUrl(participant, 32)} sx={{ bgcolor: 'primary.main' }}>
                  {participant.name?.[0] || '?'}
                </Avatar>
              ))}
            </AvatarGroup>
          ) : (
            otherUser && <UserAvatar user={otherUser} size="small" />
          )}
        </Badge>

        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: unreadCount > 0 ? 600 : 500,
                color: 'text.primary',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {title || 'Untitled Conversation'}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                ml: 1,
                flexShrink: 0,
              }}
            >
              {getTimeLabel(lastMessageTime)}
            </Typography>
          </Stack>

          <Typography
            variant="body2"
            sx={{
              color: unreadCount > 0 ? 'text.primary' : 'text.secondary',
              fontWeight: unreadCount > 0 ? 500 : 400,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {lastMessage ? (
              <>
                {lastMessage.sender?.name && <span style={{ fontWeight: 500 }}>{lastMessage.sender.name}: </span>}
                {truncateText(lastMessage.text)}
              </>
            ) : (
              <em>No messages yet</em>
            )}
          </Typography>
        </Box>
      </Stack>
    </ListItemButton>
  )
}
